/**
 * CR127: Unified Run Status Utilities
 * Single source of truth for determining effective merchant/unit status
 * Used by both Eye modal (PocRuns.tsx) and detail page (PocRunDetail.tsx)
 *
 * CR416: Centralized POC run enum-safe constants
 * poc_run_status enum labels: queued | running | completed | failed | canceled
 * NEVER use 'success' or 'cancelled' when filtering poc_runs.status
 */

/**
 * CR416: Canonical poc_run_status enum values (DB source of truth)
 * Use these constants instead of string literals when querying poc_runs.status
 */
export const POC_RUN_STATUS = {
  QUEUED: "queued",
  RUNNING: "running",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELED: "canceled",
} as const;

export type PocRunStatusValue = typeof POC_RUN_STATUS[keyof typeof POC_RUN_STATUS];

/** Terminal statuses — run is done (no more steps will execute) */
export const POC_RUN_TERMINAL_STATUSES: PocRunStatusValue[] = [
  POC_RUN_STATUS.COMPLETED,
  POC_RUN_STATUS.FAILED,
  POC_RUN_STATUS.CANCELED,
];

/** Active statuses — run may still produce work */
export const POC_RUN_ACTIVE_STATUSES: PocRunStatusValue[] = [
  POC_RUN_STATUS.QUEUED,
  POC_RUN_STATUS.RUNNING,
];

/** Check if a poc_runs.status value is terminal */
export function isPocRunTerminal(status: string): boolean {
  return POC_RUN_TERMINAL_STATUSES.includes(status as PocRunStatusValue);
}

/** Human-friendly label for poc_run_status enum values */
export const POC_RUN_STATUS_LABELS: Record<string, string> = {
  queued: "Queued",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
  canceled: "Canceled",
};

export type EffectiveStatus = 
  | "running" 
  | "succeeded" 
  | "failed" 
  | "timed_out" 
  | "pending" 
  | "skipped"
  | "unknown";

export type RunOverallStatus = 
  | "RUNNING" 
  | "SUCCEEDED" 
  | "FAILED" 
  | "COMPLETED_WITH_ERRORS";

export interface MerchantLike {
  status?: string | null;
  error_message?: string | null;
  read_status?: string | null;
  read_failure_reason?: string | null;
}

export interface UnitLike {
  status: string;
  error_message?: string | null;
}

export interface RunSummary {
  runId: string;
  overallStatus: RunOverallStatus;
  totals: {
    pagesScanned: number;
    candidates: number;
    dealsCreated: number;
    succeededMerchants: number;
    failedMerchants: number;
    totalMerchants: number;
  };
  hasPartialFailure: boolean;
}

/**
 * Determine effective status for a merchant record
 * Checks multiple fields that can indicate failure
 */
export function getEffectiveMerchantStatus(merchant: MerchantLike): EffectiveStatus {
  // Check for explicit failure reasons
  if (merchant.read_failure_reason) {
    return "failed";
  }
  
  // Check read_status for failed states
  const readStatus = merchant.read_status?.toUpperCase();
  if (readStatus === "UNREADABLE_SOURCE" || readStatus === "SKIPPED") {
    return "failed";
  }
  if (readStatus === "TIMED_OUT") {
    return "timed_out";
  }
  
  // Check for error_message on "completed" status - indicates failure
  const status = merchant.status?.toLowerCase() || "";
  if (merchant.error_message && (status === "completed" || status === "complete")) {
    return "failed";
  }
  
  // Normalize status strings
  if (status === "completed" || status === "complete" || status === "success" || status === "succeeded") {
    return "succeeded";
  }
  if (status === "failed" || status === "failed_unknown" || status === "error") {
    return "failed";
  }
  if (status === "running" || status === "in_progress") {
    return "running";
  }
  if (status === "pending" || status === "not_started") {
    return "pending";
  }
  if (status === "skipped") {
    return "skipped";
  }
  if (status === "timed_out") {
    return "timed_out";
  }
  
  return "unknown";
}

/**
 * Determine effective status for a unit record (poc_run_units)
 */
export function getEffectiveUnitStatus(unit: UnitLike): EffectiveStatus {
  // Check for error on completed units
  if (unit.error_message && unit.status?.toUpperCase() === "COMPLETE") {
    return "failed";
  }
  
  const status = unit.status?.toUpperCase() || "";
  
  if (status === "COMPLETE" || status === "SUCCEEDED") {
    return "succeeded";
  }
  if (status === "FAILED") {
    return "failed";
  }
  if (status === "TIMED_OUT") {
    return "timed_out";
  }
  if (status === "RUNNING") {
    return "running";
  }
  if (status === "NOT_STARTED") {
    return "pending";
  }
  
  return "unknown";
}

/**
 * Check if a status represents a failure
 */
export function isFailedStatus(status: EffectiveStatus): boolean {
  return status === "failed" || status === "timed_out";
}

/**
 * Check if a status represents success
 */
export function isSucceededStatus(status: EffectiveStatus): boolean {
  return status === "succeeded";
}

/**
 * Determine overall run status from merchants/units
 */
export function computeOverallStatus(
  succeeded: number,
  failed: number,
  running: number
): RunOverallStatus {
  if (running > 0) {
    return "RUNNING";
  }
  if (failed > 0 && succeeded > 0) {
    return "COMPLETED_WITH_ERRORS";
  }
  if (failed > 0 && succeeded === 0) {
    return "FAILED";
  }
  return "SUCCEEDED";
}

/**
 * Status display configuration
 */
export const STATUS_DISPLAY: Record<EffectiveStatus, { color: string; label: string }> = {
  running: { color: "bg-blue-500", label: "Running" },
  succeeded: { color: "bg-green-500", label: "Succeeded" },
  failed: { color: "bg-red-500", label: "Failed" },
  timed_out: { color: "bg-orange-500", label: "Timed Out" },
  pending: { color: "bg-gray-400", label: "Pending" },
  skipped: { color: "bg-gray-400", label: "Skipped" },
  unknown: { color: "bg-gray-500", label: "Unknown" },
};

// Extended status colors for backwards compatibility
export const STATUS_COLORS: Record<string, string> = {
  pending: "bg-gray-500",
  running: "bg-blue-500",
  success: "bg-green-500",
  completed: "bg-green-500",
  succeeded: "bg-green-500",
  complete: "bg-green-500",
  failed: "bg-red-500",
  failed_unknown: "bg-red-500",
  error: "bg-red-500",
  canceled: "bg-yellow-500",
  skipped: "bg-gray-400",
  timed_out: "bg-orange-500",
  unknown: "bg-gray-500",
};
